// ✅ Trekkin.app — FloatingButtonBar.jsx v6.7.5 (FINAL-FINAL)
// 🔹 LED verde ativo igual ao design original
// 🔹 LED APAGA quando painel fecha
// 🔹 Usa toggleButtonPanel corretamente (key, key)
// 🔹 Compatível com PanelShell v3.1.0 e usePanelsStore v6.5.x
// 🔹 Botão "admin" visível para planos/roles admin/guide (role OU plan)
// 🔹 Regra de Ouro: ficheiro completo

import React, { useEffect, useMemo, useState } from "react";
import { usePanelsStore } from "../stores/usePanelsStore";
import { useAuthStore } from "../stores/useAuthStore";
import TrekkinChart from "./TrekkinChart.jsx";
import { useTranslate } from "../i18n/useTranslate";
import {
  trekkinBubbleStyle,
  trekkinBubbleIcon,
} from "../styles/TrekkinPanelStyles.js";

const labeledMapBubbleStyle = {
  ...trekkinBubbleStyle,
  width: 52,
  minWidth: 52,
  maxWidth: 52,
  height: 52,
  minHeight: 52,
  maxHeight: 52,
  flex: "0 0 52px",
  flexDirection: "column",
  gap: 1,
  padding: "4px 2px 3px",
  boxSizing: "border-box",
  overflow: "hidden",
  color: "#111111",
};
const labeledMapBubbleIcon = { ...trekkinBubbleIcon, width: 24, height: 24, flex: "0 0 auto" };
const mapBubbleLabelStyle = { color: "#111111", fontSize: 8.5, lineHeight: 1, fontWeight: 800, textAlign: "center", whiteSpace: "nowrap" };

const getPointLon = (point) => point?.lon ?? point?.lng;

const validRoute = (points) =>
  Array.isArray(points) &&
  points.filter((point) => {
    const lat = Number(point?.lat);
    const lon = Number(getPointLon(point));
    return Number.isFinite(lat) && Number.isFinite(lon);
  }).length > 1;

const routeSignature = (points) => {
  if (!validRoute(points)) return "empty";
  const first = points[0] || {};
  const mid = points[Math.floor(points.length / 2)] || {};
  const last = points[points.length - 1] || {};
  const format = (point) => {
    const lat = Number(point?.lat);
    const lon = Number(getPointLon(point));
    return `${Number.isFinite(lat) ? lat.toFixed(6) : "x"},${
      Number.isFinite(lon) ? lon.toFixed(6) : "x"
    }`;
  };
  return `${points.length}:${format(first)}:${format(mid)}:${format(last)}`;
};

const nearestRoutePoint = (points, position) => {
  if (!validRoute(points) || !position) return null;
  const lat = Number(position.lat);
  const lon = Number(getPointLon(position));
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  let bestPoint = null;
  let bestScore = Infinity;
  for (const point of points) {
    const pointLat = Number(point?.lat);
    const pointLon = Number(getPointLon(point));
    if (!Number.isFinite(pointLat) || !Number.isFinite(pointLon)) continue;
    const dLat = pointLat - lat;
    const dLon = pointLon - lon;
    const score = dLat * dLat + dLon * dLon;
    if (score < bestScore) {
      bestScore = score;
      bestPoint = point;
    }
  }

  if (!bestPoint) return null;
  return { lat: Number(bestPoint.lat), lon: Number(getPointLon(bestPoint)) };
};

export default function FloatingButtonBar() {
  const translate = useTranslate();
  const { toggleButtonPanel, currentPanel } = usePanelsStore();
  const points = usePanelsStore((s) => s.points);
  const userPosition = usePanelsStore((s) => s.userPosition);
  const trailBase = usePanelsStore((s) => s.trailBase);
  const followProfilePoints = usePanelsStore((s) => s.followProfilePoints);
  const navWaypointTarget = usePanelsStore((s) => s.navWaypointTarget);
  const navWaypointPending = usePanelsStore((s) => s.navWaypointPending);
  const planActive = usePanelsStore((s) => s.planActive);
  const { user } = useAuthStore();
  const [adminUserView, setAdminUserView] = useState(() => {
    try {
      return window.localStorage.getItem("trekkin_admin_user_view_v1") === "1";
    } catch {
      return false;
    }
  });
  const [waypointRoute, setWaypointRoute] = useState(() => {
    if (typeof window === "undefined") return [];
    const path = window.__trekkinLastNavGridRoute?.path;
    return Array.isArray(path) ? path : [];
  });

  const active = currentPanel?.name || null;
  const base = import.meta.env.BASE_URL || "/";

  const role = String(user?.role || "").toLowerCase();
  const canSeeAdmin = role === "admin" && !adminUserView;

  useEffect(() => {
    const sync = (event) => {
      if (typeof event?.detail?.enabled === "boolean") {
        setAdminUserView(event.detail.enabled);
        return;
      }
      try {
        setAdminUserView(window.localStorage.getItem("trekkin_admin_user_view_v1") === "1");
      } catch {
        setAdminUserView(false);
      }
    };
    window.addEventListener("storage", sync);
    window.addEventListener("trekkin:admin-user-view", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("trekkin:admin-user-view", sync);
    };
  }, []);

  const buttons = [
    { key: "nav", icon: "navigation.svg", label: translate("bars.navigate") },
    { key: "trail", icon: "trail.svg", label: translate("bars.events") },
    { key: "guide", icon: "guide.svg", label: translate("bars.guides") },
    { key: "settings", icon: "user-settings.svg", label: translate("bars.account") },
    { key: "admin", icon: "admin.svg", label: translate("bars.admin") },
  ];

  // Filtra o admin se o user não tiver permissão
  const visibleButtons = buttons.filter(
    (btn) => btn.key !== "admin" || canSeeAdmin
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleRoute = (event) => {
      const path = event?.detail?.path;
      setWaypointRoute(Array.isArray(path) ? path : []);
      // O mesmo evento desenha a pré-visualização do criador e uma rota de
      // navegação. Uma rota ainda em edição nunca pode ser persistida como
      // navegação ativa.
      if (!usePanelsStore.getState().planActive) {
        usePanelsStore.getState().persistNavigationRoute?.(event?.detail || {});
      }
    };
    window.addEventListener("trekkin:navgrid-route", handleRoute);
    return () => window.removeEventListener("trekkin:navgrid-route", handleRoute);
  }, []);

  const lastPoint = useMemo(() => {
    if (!Array.isArray(points) || points.length === 0) return null;
    return points[points.length - 1] || null;
  }, [points]);

  const followPosition = userPosition || lastPoint || null;
  const followLat = Number(followPosition?.lat);
  const followLon = Number(getPointLon(followPosition));

  const navigationProfile = useMemo(() => {
    const waypointNavigationActive = !!navWaypointTarget || !!navWaypointPending;
    if (waypointNavigationActive && validRoute(waypointRoute)) {
      return waypointRoute;
    }
    if (validRoute(trailBase)) {
      return trailBase;
    }
    return validRoute(followProfilePoints) ? followProfilePoints : [];
  }, [
    followProfilePoints,
    navWaypointPending,
    navWaypointTarget,
    trailBase,
    waypointRoute,
  ]);

  const navigationProfileSignature = useMemo(
    () => routeSignature(navigationProfile),
    [navigationProfile]
  );

  const navigationProgressPoint = useMemo(() => {
    if (!Number.isFinite(followLat) || !Number.isFinite(followLon)) return null;
    return nearestRoutePoint(navigationProfile, { lat: followLat, lon: followLon });
  }, [
    followLat,
    followLon,
    navigationProfile,
  ]);

  const showNavigationProfile =
    !planActive && navigationProfile.length > 1 && !currentPanel;

  return (
    <div
      className="tk-floating-bar"
      style={{
        position: "fixed",
        bottom: "calc(2px + env(safe-area-inset-bottom, 0px))",
        left: 0,
        right: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        zIndex: 900,
        pointerEvents: "none",
      }}
    >
      {showNavigationProfile ? (
        <div
          aria-hidden="true"
          style={{
            width: "min(520px, calc(100vw - 24px))",
            height: 86,
            pointerEvents: "none",
          }}
        >
          <TrekkinChart
            key={navigationProfileSignature}
            height={86}
            points={navigationProfile}
            useElevationMap
            background="transparent"
            emptyBackground="transparent"
            emptyColor="transparent"
            lineColor="#ef233c"
            lineWidth={3}
            lineOutlineColor="rgba(255,255,255,0.95)"
            lineOutlineWidth={6}
            showProgressOverlay={!!navigationProgressPoint}
            progressPoint={navigationProgressPoint}
            progressColor="#1f78ff"
            progressLineWidth={3}
            padding={0}
            borderRadius={0}
          />
        </div>
      ) : null}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 14,
          pointerEvents: "auto",
        }}
      >
        {visibleButtons.map((btn) => {
          const isActive = active === btn.key;

          return (
            <button
              key={btn.key}
              onClick={() => toggleButtonPanel(btn.key, btn.key)}
              style={{
                ...labeledMapBubbleStyle,
                background: isActive
                  ? "rgba(40,167,69,0.22)"
                  : trekkinBubbleStyle.background,
                boxShadow: isActive
                  ? "0 0 10px rgba(40,167,69,0.8), 0 0 18px rgba(40,167,69,0.55)"
                  : trekkinBubbleStyle.boxShadow,
                transition: "all 0.25s ease",
              }}
            >
              <img
                src={`${base}icons/${btn.icon}`}
                alt={btn.key}
                style={{
                  ...labeledMapBubbleIcon,
                  filter: isActive ? "drop-shadow(0 0 4px #28a745)" : "none",
                }}
              />
              <span style={mapBubbleLabelStyle}>{btn.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
